<template>
  <div class="reports inside-banner mobile-top">
    <!-- ============ BANNER ============ -->
    <section class="pixelsbggrid bs-shadow report-banner">
      <!-- Top row: back, title, CSV -->
      <div class="banner-top">
        <back-button />
        <h4 class="banner-title text-white mb0">
          {{ loading ? "Loading Report Details" : report?.header2 || "Report" }}
        </h4>
        <download-csv
          v-if="!loading && report"
          class="mb0 lh1 list-btn dib d-flex center"
          :data="sortedRows"
          :name="csvName"
        >
          <span class="material-icons">download</span> CSV
        </download-csv>
      </div>

      <!-- Stat cards -->
      <div class="bucket-row">
        <div v-for="card in statCards" :key="card.label" class="bucket">
          <h5 class="bucket-title mb0">{{ card.label }}</h5>
          <span class="material-icons bucket-arrow">keyboard_arrow_down</span>
          <h3 v-if="loading" class="bucket-value is-loading">Loading....</h3>
          <h3 v-else class="bucket-value">{{ card.value }}</h3>
          <span v-if="!loading && card.sub" class="bucket-sub">{{ card.sub }}</span>
        </div>
      </div>
    </section>

    <div class="report-card">
      <!-- ============ LOADING ============ -->
      <div v-if="loading" class="state-box">
        <atom-spinner :animation-duration="1000" :size="40" :color="'#575757'" />
        <p class="mb0 mt10">Loading......</p>
        <p class="text-muted">Please be patient while we look up your data.</p>
      </div>

      <!-- ============ ERROR ============ -->
      <div v-else-if="loadError || !report" class="state-box">
        <span class="material-icons state-icon">error_outline</span>
        <p class="mb0">Sorry, we couldn't load this report.</p>
        <button class="list-btn mt10" @click="firstLoad">Try again</button>
      </div>

      <template v-else>
        <!-- ============ FILTERS ============ -->
        <div v-if="filterChips.length" class="filter-row">
          <span class="filter-heading">Viewing orders for</span>
          <span v-for="chip in filterChips" :key="chip.label" class="filter-chip">
            <span class="material-icons">{{ chip.icon }}</span>
            <span class="chip-label">{{ chip.label }}:</span> {{ chip.value }}
          </span>
        </div>

        <div v-if="statusCounts.length > 1" class="status-filter">
          <button :class="['status-chip', { active: !statusFilter }]" @click="statusFilter = ''">
            All <span class="count">{{ allRows.length }}</span>
          </button>
          <button
            v-for="s in statusCounts"
            :key="s.status"
            :class="['status-chip', { active: statusFilter === s.status }]"
            @click="statusFilter = statusFilter === s.status ? '' : s.status"
          >
            {{ s.status }} <span class="count">{{ s.count }}</span>
          </button>
        </div>

        <!-- ============ TABLE ============ -->
        <div v-if="sortedRows.length" class="table-wrap">
          <table class="report-table" :style="{ minWidth: tableMinWidth }">
            <colgroup>
              <col v-for="col in columns" :key="col.key" :style="{ width: col.width + 'px' }" />
            </colgroup>
            <thead>
              <tr>
                <th
                  v-for="col in columns"
                  :key="col.key"
                  scope="col"
                  :class="['table-link', { 'column-active': sortKey === col.key }]"
                  :aria-sort="sortKey === col.key ? (sortDir === 1 ? 'ascending' : 'descending') : 'none'"
                  @click="sortCol(col.key)"
                >
                  <span class="th-inner">
                    <span class="th-label">{{ col.label }}</span>
                    <span class="ml-auto">
                      <i
                        :class="[
                          'material-icons carrot-icon',
                          { 'carrot-icon-active': sortKey === col.key, 'is-desc': sortKey === col.key && sortDir === -1 }
                        ]"
                      >filter_list</i>
                    </span>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in sortedRows" :key="i">
                <td>
                  <router-link :to="`/order-details/${row.orderNo}`" class="order-link">{{ row.orderNo }}</router-link>
                </td>
                <td class="wrap" v-html="row.projName"></td>
                <td :title="clean(row.salesman)">{{ clean(row.salesman) }}</td>
                <td class="text-right">
                  <template v-if="clean(row.salesCreditPercent) !== ''">{{ row.salesCreditPercent }}%</template>
                </td>
                <td :title="clean(row.customerPoNo)">{{ clean(row.customerPoNo) }}</td>
                <td>{{ clean(row.marketCode) }}</td>
                <td><span :class="['status-pill', statusClass(row.status)]">{{ row.status }}</span></td>
                <td>{{ formatDate(row.dateEntered) }}</td>
                <td><template v-if="showPromised(row.status)">{{ formatDate(row.datePromised) }}</template></td>
                <td class="text-right">{{ formatPrice(row.individualAmount) }}</td>
                <td class="wrap">{{ clean(row.acName) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- ============ EMPTY ============ -->
        <div v-else class="state-box">
          <span class="material-icons state-icon">search_off</span>
          <p class="mb0">No orders match your filters.</p>
          <button v-if="isFiltered" class="list-btn mt10" @click="resetFilters">Clear filters</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { AtomSpinner } from "epic-spinners";
import JsonCSV from "vue-json-csv";
import { formatCurrency } from "../utils/utils";
import BackButton from "@/components/Buttons/BackButton.vue";

const auth = {
  headers: {
    Authorization: "Bearer " + localStorage.getItem("SSCUSER"),
  },
};

// Statuses where a promised date is meaningful
const PROMISED_STATUSES = new Set([
  "In Production",
  "Released",
  "Partially Shipped",
  "Shipped",
  "In Shipping",
]);

export default {
  name: "ReportDetails",
  components: {
    AtomSpinner,
    BackButton,
    JsonCSV,
  },
  data() {
    return {
      ordermanageapi: import.meta.env.VITE_APP_ORDER_MANAGE_API,
      report: null,
      loading: true,
      loadError: false,

      search: "",
      statusFilter: "",
      sortKey: "",
      sortDir: 1, // 1 = ascending, -1 = descending

      columns: [
        { key: "orderNo",            label: "Order",    width: 90 },
        { key: "projName",           label: "Project",  width: 260 },
        { key: "salesman",           label: "Rep",      width: 170 },
        { key: "salesCreditPercent", label: "Sales %",  width: 85,  type: "number", align: "right" },
        { key: "customerPoNo",       label: "PO",       width: 120 },
        { key: "marketCode",         label: "Market",   width: 85 },
        { key: "status",             label: "Status",   width: 135 },
        { key: "dateEntered",        label: "Entered",  width: 105, type: "date" },
        { key: "datePromised",       label: "Promised", width: 105, type: "date" },
        { key: "individualAmount",   label: "Credit",   width: 115, type: "number", align: "right" },
        { key: "acName",             label: "AC",       width: 200 },
      ],
    };
  },

  computed: {
    statCards() {
  return [
    {
      label: "Orders",
      value: this.sortedRows.length.toLocaleString(),
      sub: this.isFiltered ? `of ${this.allRows.length.toLocaleString()}` : "",
    },
    { label: "Total", value: this.formatPrice(this.totalCredit) },
    { label: "Area Contractors", value: this.uniqueACs },
    { label: "Reps", value: this.uniqueReps },
  ];
},
    tableMinWidth() {
      return this.columns.reduce((sum, c) => sum + (c.width || 100), 0) + "px";
    },
    allRows() {
      return this.report?.details || [];
    },

    isFiltered() {
      return !!(this.search || this.statusFilter);
    },

    filteredRows() {
      const q = this.search.toLowerCase();
      return this.allRows.filter((r) => {
        if (this.statusFilter && r.status !== this.statusFilter) return false;
        if (!q) return true;
        return [
          r.orderNo,
          this.stripHtml(r.projName),
          r.salesman,
          r.customerPoNo,
          r.marketCode,
          r.status,
          r.acName,
        ].some((v) => String(v ?? "").toLowerCase().includes(q));
      });
    },

    // Sorts a copy, so the original API order is kept
    sortedRows() {
      const col = this.columns.find((c) => c.key === this.sortKey);
      if (!col) return this.filteredRows;

      return [...this.filteredRows].sort((a, b) => {
        const x = this.sortValue(a, col);
        const y = this.sortValue(b, col);
        if (x === y) return 0;
        if (x === null) return 1; // blanks always last
        if (y === null) return -1;
        const cmp =
          typeof x === "number" && typeof y === "number"
            ? x - y
            : String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: "base" });
        return cmp * this.sortDir;
      });
    },

    totalCredit() {
      return this.sortedRows.reduce((sum, r) => sum + (Number(r.individualAmount) || 0), 0);
    },

    uniqueACs() {
      return new Set(this.sortedRows.map((r) => r.acName).filter(Boolean)).size;
    },

    uniqueReps() {
  const isHouse = (name) => /^house\b/i.test(name);

  return new Set(
    this.sortedRows
      .map((r) => String(this.clean(r.salesman)).trim().replace(/\s+/g, " "))
      .filter((name) => name && !isHouse(name))
      .map((name) => name.toLowerCase())
  ).size;
},

    statusCounts() {
      const counts = {};
      this.allRows.forEach((r) => {
        if (r.status) counts[r.status] = (counts[r.status] || 0) + 1;
      });
      return Object.entries(counts)
        .map(([status, count]) => ({ status, count }))
        .sort((a, b) => b.count - a.count);
    },

    // Number of columns before "Credit", for the footer colspan
    creditColIndex() {
      return this.columns.findIndex((c) => c.key === "individualAmount");
    },

    filterChips() {
      const q = this.$route.query;
      const chips = [];
      const month = (m) => (m && /^\d+$/.test(m) ? this.$moment().month(Number(m) - 1).format("MMM") : m);

      if (q.repid) chips.push({ icon: "assignment_ind", label: "Sales Rep", value: q.repid });
      if (q.status) chips.push({ icon: "flag", label: "Status", value: q.status });
      if (q.marketcode) chips.push({ icon: "storefront", label: "Market", value: q.marketcode });
      if (q.territory) chips.push({ icon: "map", label: "Territory", value: q.territory });
      if (q.inmonth || q.inyear)
        chips.push({
          icon: "event",
          label: "Entered",
          value: [month(q.inmonth), q.inyear].filter(Boolean).join(" "),
        });
      if (q.shippedmonth || q.shippedyear)
        chips.push({
          icon: "local_shipping",
          label: "Shipped",
          value: [month(q.shippedmonth), q.shippedyear].filter(Boolean).join(" "),
        });
      return chips;
    },

    csvName() {
      const base = (this.report?.header2 || "report")
        .replace("All Orders with status of ", "")
        .replace(/[\\/:*?"<>|]/g, "-")
        .trim();
      return `${base}.csv`;
    },
  },

  methods: {
    firstLoad() {
      this.loading = true;
      this.loadError = false;
      this.$store.commit("setActivePage", "order-manage");

      return axios
        .get(`${this.ordermanageapi}${window.location.pathname + window.location.search}`, auth)
        .then((response) => {
          this.report = response.data;
        })
        .catch((err) => {
          console.log(err);
          this.loadError = true;
        })
        .finally(() => {
          this.loading = false;
          document.getElementById("mobileScrollTarget")?.scrollIntoView({ behavior: "smooth" });
        });
    },

    // Same column toggles direction; a new column starts ascending
    sortCol(key) {
      if (this.sortKey === key) {
        this.sortDir *= -1;
      } else {
        this.sortKey = key;
        this.sortDir = 1;
      }
    },

    sortValue(row, col) {
      let v = row[col.key];
      if (v === null || v === undefined || v === "" || v === "null") return null;
      if (col.key === "projName") v = this.stripHtml(v);
      if (col.type === "date") {
        const t = new Date(v).getTime();
        return isNaN(t) ? null : t;
      }
      if (col.type === "number") {
        const n = Number(v);
        return isNaN(n) ? null : n;
      }
      return v;
    },

    resetFilters() {
      this.search = "";
      this.statusFilter = "";
    },

    showPromised(status) {
      return PROMISED_STATUSES.has(status);
    },

    statusClass(status) {
      return "status-" + String(status || "").toLowerCase().replace(/\s+/g, "-");
    },

    clean(v) {
      return v === null || v === undefined || v === "null" ? "" : v;
    },

    stripHtml(html) {
      return String(html ?? "").replace(/<[^>]*>/g, "");
    },

    formatDate(v) {
      if (!v) return "";
      const d = this.$moment(v);
      return d.isValid() ? d.format("MM-DD-YYYY") : "";
    },

    formatPrice(amount) {
      return formatCurrency(amount);
    },
  },

  created() {
    this.$store.commit("showNavigation");
  },
  mounted() {
    document.getElementById("mobileScrollTarget")?.scrollIntoView({ behavior: "smooth" });
    this.firstLoad();
  },
};
</script>

<style scoped>
/* ---------- Banner ---------- */
.report-banner {
  padding: 20px 20px 10px;
}
.banner-top {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}
.banner-title {
  margin-right: auto;
  font-weight: 400;
}
.btn-icon {
  font-size: 18px;
  margin-right: 4px;
}

/* Stat cards (same look as the main reports header) */
.bucket-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
  margin-bottom: 10px;
}
.bucket {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 10px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}
.bucket-title {
  color: #fff;
  font-weight: 400;
}
.bucket-arrow {
  font-size: 16px;
  opacity: 0.8;
}
.bucket-value {
  color: #fff;
  margin: 0;
  font-size: 1.5rem;
}
.bucket-value.is-loading {
  color: rgba(255, 255, 255, 0.6);
}
.bucket-sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.75);
}

/* ---------- Card ---------- */
.report-card {
  background: #fff;
  padding: 20px;
  border-radius: 4px;
  min-height: 600px;
}

/* ---------- Filters ---------- */
.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}
.filter-heading {
  font-size: 13px;
  color: #757575;
  margin-right: 4px;
}
.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 14px;
  background: #f5f6f7;
  color: #4d5967;
  font-size: 13px;
}
.filter-chip .material-icons {
  font-size: 15px;
  color: #00b4b4;
}
.chip-label {
  opacity: 0.7;
}

.status-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;
}
.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid #dcdcdc;
  border-radius: 14px;
  background: #fff;
  color: #4d5967;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}
.status-chip:hover {
  border-color: #00b4b4;
}
.status-chip.active {
  background: #00b4b4;
  border-color: #00b4b4;
  color: #fff;
}
.status-chip .count {
  font-size: 11px;
  padding: 0 6px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.08);
}
.status-chip.active .count {
  background: rgba(255, 255, 255, 0.25);
}

/* ---------- Table ---------- */
.table-wrap {
  overflow: auto;
  max-height: calc(100vh - 220px);
  border: 1px solid #e8e8e8;
  border-radius: 4px;
}
.report-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 14px;
}
.report-table th,
.report-table td {
  padding: 5px 8px;
  border: 1px solid #e8e8e8;
  text-align: left;
  vertical-align: middle;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.report-table td.wrap {
  white-space: normal;
  overflow-wrap: anywhere;
}
.report-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #4d5967;
  color: #fff;
  font-weight: 300;
  cursor: pointer;
  user-select: none;
}
.report-table thead th:hover {
  background: #586575;
}
.report-table tbody tr:nth-of-type(odd) {
  background: rgba(0, 0, 0, 0.025);
}
.report-table tbody tr:hover {
  background: rgba(0, 180, 180, 0.08);
}
.th-inner {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.th-label {
  overflow: hidden;
  text-overflow: ellipsis;
}
.ml-auto {
  margin-left: auto;
  display: flex;
}
.carrot-icon {
  font-size: 18px;
  line-height: 1;
  flex-shrink: 0;
  opacity: 0.6;
  transition: transform 0.15s;
}
.carrot-icon-active {
  opacity: 1;
  color: #00e0e0;
}
.carrot-icon.is-desc {
  transform: scaleY(-1);
}
.text-right {
  text-align: right !important;
}
.order-link {
  color: #00b4b4;
  font-weight: 500;
}

.status-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  white-space: nowrap;
  background: #eef0f2;
  color: #4d5967;
}

/* ---------- States ---------- */
.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 20px;
  text-align: center;
  color: #757575;
}
.state-icon {
  font-size: 40px;
  color: #bbb;
  margin-bottom: 8px;
}
.mt10 {
  margin-top: 10px;
}

@media (max-width: 700px) {
  .banner-title {
    flex-basis: 100%;
    order: -1;
  }
  .bucket-row {
    grid-template-columns: repeat(2, 1fr);
  }
  .table-wrap {
    max-height: none;
  }
}
 .banner-top > :not(.banner-title) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 14px;
  margin: 0;
  line-height: 1;
  font-size: 14px;
  box-sizing: border-box;
  white-space: nowrap;
}
.banner-top > :not(.banner-title) :deep(.material-icons) {
  font-size: 20px;
  margin-right: 4px;
}
.pixelsbggrid {
  background-color: #475059;
  background-size: cover;
  background-position: center;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1950 1200' preserveAspectRatio='xMidYMid slice'%3E%3Cdefs%3E%3CradialGradient id='a' cx='0' cy='1200' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%2356626d'/%3E%3Cstop offset='1' stop-color='%2356626d' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='b' cx='1950' cy='1200' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%2337778a'/%3E%3Cstop offset='1' stop-color='%2337778a' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='c' cx='975' cy='0' r='1200' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%232b5c60'/%3E%3Cstop offset='1' stop-color='%232b5c60' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='d' cx='975' cy='1200' r='1200' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%23667787'/%3E%3Cstop offset='1' stop-color='%23667787' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='e' cx='0' cy='0' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%23475059'/%3E%3Cstop offset='1' stop-color='%23475059' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='f' cx='1950' cy='0' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%231a8182'/%3E%3Cstop offset='1' stop-color='%231a8182' stop-opacity='0'/%3E%3C/radialGradient%3E%3ClinearGradient id='base' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0' stop-color='%23475059'/%3E%3Cstop offset='.5' stop-color='%2356626d'/%3E%3Cstop offset='1' stop-color='%231a8182'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect fill='%23475059' width='1950' height='1200'/%3E%3Crect fill='url(%23base)' width='1950' height='1200'/%3E%3Cline x1='0' y1='0' x2='0' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='50' y1='0' x2='50' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='100' y1='0' x2='100' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='150' y1='0' x2='150' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='200' y1='0' x2='200' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='250' y1='0' x2='250' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='300' y1='0' x2='300' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='350' y1='0' x2='350' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='400' y1='0' x2='400' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='450' y1='0' x2='450' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='500' y1='0' x2='500' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='550' y1='0' x2='550' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='600' y1='0' x2='600' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='650' y1='0' x2='650' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='700' y1='0' x2='700' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='750' y1='0' x2='750' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='800' y1='0' x2='800' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='850' y1='0' x2='850' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='900' y1='0' x2='900' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='950' y1='0' x2='950' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1000' y1='0' x2='1000' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='1050' y1='0' x2='1050' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1100' y1='0' x2='1100' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1150' y1='0' x2='1150' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1200' y1='0' x2='1200' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1250' y1='0' x2='1250' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='1300' y1='0' x2='1300' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1350' y1='0' x2='1350' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1400' y1='0' x2='1400' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1450' y1='0' x2='1450' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1500' y1='0' x2='1500' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='1550' y1='0' x2='1550' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1600' y1='0' x2='1600' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1650' y1='0' x2='1650' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1700' y1='0' x2='1700' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1750' y1='0' x2='1750' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='1800' y1='0' x2='1800' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1850' y1='0' x2='1850' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1900' y1='0' x2='1900' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='0' x2='1950' y2='0' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='0' y1='50' x2='1950' y2='50' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='100' x2='1950' y2='100' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='150' x2='1950' y2='150' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='200' x2='1950' y2='200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='250' x2='1950' y2='250' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='0' y1='300' x2='1950' y2='300' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='350' x2='1950' y2='350' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='400' x2='1950' y2='400' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='450' x2='1950' y2='450' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='500' x2='1950' y2='500' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='0' y1='550' x2='1950' y2='550' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='600' x2='1950' y2='600' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='650' x2='1950' y2='650' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='700' x2='1950' y2='700' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='750' x2='1950' y2='750' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='0' y1='800' x2='1950' y2='800' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='850' x2='1950' y2='850' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='900' x2='1950' y2='900' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='950' x2='1950' y2='950' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='1000' x2='1950' y2='1000' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='0' y1='1050' x2='1950' y2='1050' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='1100' x2='1950' y2='1100' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='1150' x2='1950' y2='1150' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cg fill-opacity='0.45'%3E%3Crect fill='url(%23a)' width='1950' height='1200'/%3E%3Crect fill='url(%23b)' width='1950' height='1200'/%3E%3Crect fill='url(%23c)' width='1950' height='1200'/%3E%3Crect fill='url(%23d)' width='1950' height='1200'/%3E%3Crect fill='url(%23e)' width='1950' height='1200'/%3E%3Crect fill='url(%23f)' width='1950' height='1200'/%3E%3C/g%3E%3C/svg%3E");
}
</style>
